/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable @typescript-eslint/no-misused-promises */
// @ts-strict-ignore
/**
 * @module DataChannels
 */

// *********************************************
// ** TO DO MCO
// ** Add timeout on proposed (on both sides)
// *********************************************

import { EventableService, makeServiceEvent, RbServiceEvent, Service } from 'rainbow-web-sdk/services/service';
import { AuthService, LoggerService, MainService, XmppService } from '../core';
import { Subject, Subscription, filter } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { DataChannel, DataChannelEvents, DataChannelID, DataChannelStatus, DataChannel_RB } from 'rainbow-web-sdk/models/dataChannel.model';
import { JingleSession } from 'rainbow-web-sdk/libs/strophe/strophe.jingle.session';
import { BUBBLE_CONFERENCE_SVC, BubbleConferenceServiceRB } from '../conference/bubbleConference.service';
import { PromiseQueue } from 'rainbow-web-sdk/utils/promiseQueue';
import xmlParser, { XMLElement } from 'rainbow-web-sdk/utils/xmlParser';
import { BUBBLE_SVC, BubbleServiceRB } from '../bubbles/bubble.service';
import { ContactService } from '../core/contact.service';
import { Bubble, BubbleConferenceStatus, User } from 'rainbow-web-sdk/models';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { BubbleRB } from 'rainbow-web-sdk/models/bubble.model';
import { $iq, $msg } from 'strophe.js';
import { stringifyError } from '../../utils/stringifyError';

/** @internal */
export const DATA_CHANNEL_SVC = 'DataChannelService';

/**
 * @eventProperty
 */
export interface IDataChannelServiceEvents {
    /**
     * This RB even is send when a datachannel is available in a conversation
     */
    ON_DATACHANNEL_AVAILABLE: DataChannel;

    /**
     * This RB event is send when datachannel is deleted in a conversation
     */
    ON_DATACHANNEL_CLOSED: DataChannel;
}

export interface DataChannelService extends EventableService<IDataChannelServiceEvents> {

    /**
     * Subscribe to updates from the service
     * @param handler - The callback function that will be subscribed to the RxJS subject
     * @param eventNames - array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(handler: (event: RBEvent<DataChannelServiceEvents>) => any, eventNames?: string | string[]): Subscription;

    /**
     * @param peer - a contact or a bubble
     * @param id - the dataChannel identifier
     * @param description - dataChannel description
     * @returns a promise which is resolved when the dataChannel is created
     */
    createDataChannel(peer: User | Bubble, id: string, description?: string): Promise<DataChannel>;
}

/**
 * @eventProperty
 * `DataChannelService` specific events
 */
export enum DataChannelServiceEvents {
    /**
     * @eventProperty ON_DATACHANNEL_AVAILABLE
     * This RB event is send when a datachannel is available in a conversation
     */
    ON_DATACHANNEL_AVAILABLE = 'ON_DATACHANNEL_AVAILABLE',

    /**
     * @eventProperty ON_DATACHANNEL_CLOSED
     * This RB event is send when a datachannel is deleted in a conversation
     */
    ON_DATACHANNEL_CLOSED = 'ON_DATACHANNEL_CLOSED'
}

/** @internal */
export class DataChannelService_RB extends Service implements DataChannelService {

    private mainService: MainService = MainService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private loggerService: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private contactService: ContactService = ContactService.getInstance();

    // Injectable services, not always available
    private webConferenceService?: BubbleConferenceServiceRB;
    private bubbleService?: BubbleServiceRB;

    private subscriptions: Subscription[] = [];
    private jingleSubscription?: Subscription;
    private conferenceEventListener?: any;
    private dataChannelEventListener?: any;

    private readonly eventSubject = new Subject<RbServiceEvent<IDataChannelServiceEvents>>();
    private promiseQueue: PromiseQueue = PromiseQueue.create();
    private readonly dataChannels = new Map<string, DataChannel_RB>();

    // **************************************************
    // ** SERVICE LYFECYCLE                            **
    // **************************************************

    public static getInstance(): DataChannelService { return Service.create(DATA_CHANNEL_SVC, DataChannelService_RB); }
    public static build(): DataChannelService { return new DataChannelService_RB(); }
    private constructor() { super(); }

    public async start(): Promise<void> {
        this.loggerService.info(`[DataChannelService] --- Starting ---`);

        this.bubbleService = this.mainService.inject<BubbleServiceRB>(BUBBLE_SVC);
        this.webConferenceService = this.mainService.inject<BubbleConferenceServiceRB>(BUBBLE_CONFERENCE_SVC);

        if (this.xmppService.connected) this.subscribeToEvents();
        this.subscriptions.push(this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
            if (event.name === 'ON_XMPP_CONNECTED' && event.data.reason !== 'PRESENCE') this.subscribeToEvents();
        }));

        this.loggerService.info(`[DataChannelService] --- Started ---`);
    }

    public async stop(): Promise<void> {
        this.loggerService.info(`[DataChannelService] stop`);
        if (this.jingleSubscription) { this.jingleSubscription.unsubscribe(); this.jingleSubscription = undefined; }
        this.subscriptions.forEach((subscription) => { subscription.unsubscribe(); });
        this.subscriptions = [];
        if (this.dataChannelEventListener) { this.xmppService.deleteHandler(this.dataChannelEventListener); this.dataChannelEventListener = undefined; }
        if (this.conferenceEventListener) { this.xmppService.deleteHandler(this.conferenceEventListener); this.conferenceEventListener = undefined; }
    }

    // **************************************************
    // ** PUBLIC API                                   **
    // **************************************************
    public events<const EventNames extends keyof IDataChannelServiceEvents>(...eventNames: EventNames[]) {
        return makeServiceEvent(this.eventSubject, ...eventNames);
    }

    public subscribe(handler: (event: RBEvent<DataChannelServiceEvents>) => any, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.eventSubject.pipe(filter((event) => {
                if (Array.isArray(eventNames)) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.eventSubject;

        return observable.subscribe((event) => {
            try { handler(event as RBEvent<DataChannelServiceEvents>); }
            catch (error) { this.loggerService.error(`[DataChannelService] subscribe () -- error -- ${stringifyError(error)}`); }
        });
    }

    public async createDataChannel(peer: User | Bubble, id: string, description?: string, affiliation?: string): Promise<DataChannel> {
        try {
            this.loggerService.info(`[DataChannelService] createDataChannel (id: ${id})`);

            // Create the Rainbow DataChannel
            const dataChannel = DataChannel_RB.create();
            dataChannel.id = id as DataChannelID;
            dataChannel.publisher = this.contactService.userContact;
            dataChannel.description = description;
            this.dataChannels.set(dataChannel.id, dataChannel);

            // Peer is a bubble, search if a session exists
            if (peer instanceof BubbleRB) {
                if (!this.webConferenceService) throw new Error(`webConferenceService not available`);
                const webConferenceSession = this.webConferenceService.getWebConferenceSessionById(peer.dbId);
                if (webConferenceSession.status !== BubbleConferenceStatus.CONNECTED) throw new Error(`WebConferenceSession status is not connected`);
                if (!webConferenceSession) throw new Error(`No webConferenceSession found for bubble ${peer.dbId}`);

                // Initiate the session
                const jingleSession: JingleSession = await this.xmppService.connection.jingle.initiateDataChannelSession(webConferenceSession.jingleJid, this.xmppService.connection.jid, webConferenceSession?.id);

                // Create RTCDataChannel
                const rtcDataChannel = jingleSession.peerconnection.createDataChannel(dataChannel.description);

                // Update dataChannel
                dataChannel.type = 'p2p';
                dataChannel.sessionId = jingleSession.sid;
                dataChannel.rtcDataChannel = rtcDataChannel;
                dataChannel.bubble = peer;
                this.attachStatusListeners(dataChannel);

                // Send offer
                jingleSession.sendOffer(true, { id: dataChannel.id, name: 'datachannel', description, affiliation });
            }

            // Peer is a contact
            else {
                const realPeer = peer as Contact;
                dataChannel.type = 'p2p';
                dataChannel.status = DataChannelStatus.PROPOSED;
                const message = $msg({ id: this.xmppService.connection.getUniqueId(null), from: this.xmppService.jid, to: realPeer.jid, type: 'normal' });
                message.c('command', { xmlns: 'jabber:iq:datachannel', node: 'data-channel-propose', id: dataChannel.id, description: dataChannel.description, publisherId: dataChannel.publisher.dbId });
                await this.xmppService.send(message);
            }

            this.loggerService.debug(`[DataChannelService] createDataChannel (id: ${dataChannel.id}) -- success`);
            return this.dataChannels.get(dataChannel.id);
        }
        catch (error) {
            this.loggerService.error(`[DataChannelService] createDataChannel -- error -- ${error.message}`);
            throw error;
        }
    }

    public async deleteDataChannel(dataChannel: DataChannel): Promise<void> {
        const dataChannelRB = dataChannel as DataChannel_RB;
        this.loggerService.info(`[DataChannelService] deleteDataChannel (id: "${dataChannelRB.id}")`);
        try {
            dataChannelRB?.rtcDataChannel?.close();
            this.xmppService.connection.jingle.terminate(dataChannelRB.sessionId);
        }
        catch (error) {
            this.loggerService.error(`[DataChannelService] closeDataChannel (id: "${dataChannelRB.id}") -- error -- ${error.message}`);
            throw error;
        }
    }

    public async leaveDataChannel(dataChannel: DataChannel): Promise<void> {
        const dataChannelRB = dataChannel as DataChannel_RB;
        this.loggerService.info(`[DataChannelService] leaveDataChannel (id: "${dataChannelRB.id}")`);
        try {
            dataChannelRB?.rtcDataChannel?.close();
            this.xmppService.connection.jingle.terminate(dataChannelRB.sessionId);
        }
        catch (error) {
            this.loggerService.error(`[DataChannelService] leaveDataChannel (id: "${dataChannelRB.id}") -- error -- ${error.message}`);
            throw error;
        }
    }

    public async acceptDataChannel(dataChannel: DataChannel): Promise<DataChannel> {
        return this.acceptDataChannelInternal(dataChannel, true);
    }

    public async refuseDataChannel(dataChannel: DataChannel): Promise<DataChannel> {
        return this.acceptDataChannelInternal(dataChannel, false);
    }

    private async acceptDataChannelInternal(dataChannel: DataChannel, accept: boolean = true): Promise<DataChannel> {
        const dataChannelRB = dataChannel as DataChannel_RB;
        const actionName = accept ? 'acceptDataChannel' : 'refuseDataChannel';
        try {
            this.loggerService.info(`[DataChannelService] ${actionName} (dataChannelId: ${dataChannelRB.id})`);

            if (dataChannelRB.bubble && accept) {

                // TEMPORAY HACK FOR LIVE TRANSCRIPTIION (NO TRANSCRIPTION USER)
                if (dataChannel.id === 'liveTranscription') {
                    dataChannelRB.status = DataChannelStatus.CONNECTING;
                    const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${dataChannelRB.bubble.dbId}/subscribe-live-transcription`;
                    const headers = this.authService.getPostHeader();
                    try {
                        const response = await fetch(url, { method: 'PUT', headers });
                        if (!response.ok) throw response;
                        const responseData = await response.json();
                        dataChannelRB.sessionId = responseData.data.sid;
                    }
                    catch (error) {
                        if (dataChannelRB) { dataChannelRB.status = DataChannelStatus.INVITED; }
                        throw error;
                    }
                }
                else {
                    const url = `${config.restServerUrl}/api/rainbow/conference/v1.0/rooms/${dataChannelRB.bubble.dbId}/users/${dataChannel.publisher.dbId}/subscribe`;
                    const headers = this.authService.getPostHeader();
                    const bodyObj: any = { media: 'application' };
                    const body = JSON.stringify(bodyObj);
                    const response = await fetch(url, { method: 'PUT', headers, body });
                    if (!response.ok) throw response;
                    const responseData = await response.json();
                    dataChannelRB.sessionId = responseData.data.sid;
                }
            }

            if (!dataChannelRB.bubble) {
                const node = accept ? 'data-channel-accept' : 'data-channel-refuse';
                const message = $iq({ id: this.xmppService.connection.getUniqueId(null), from: this.xmppService.jid, to: dataChannelRB.publisherFullJid, type: 'set' });
                message.c('command', { xmlns: 'jabber:iq:datachannel', node, id: dataChannelRB.id });
                await this.xmppService.sendIQ(message);
                if (!accept) dataChannelRB.status = DataChannelStatus.REFUSED;
            }

            return dataChannel;
        }
        catch (error) {
            this.loggerService.error(`[DataChannelService] ${actionName} (dataChannelId: ${dataChannelRB.id}) -- error -- ${error.message}`);
            throw error;
        }
    }

    // **************************************************
    // ** EVENTS HANDLING
    // **************************************************

    private subscribeToEvents(): void {
        this.loggerService.info(`[DataChannelService] subscribeToEvents`);

        if (this.jingleSubscription) { this.jingleSubscription.unsubscribe(); this.jingleSubscription = undefined; }
        this.jingleSubscription = this.xmppService.connection.jingle.rxSubject.subscribe((evt: RBEvent) => { this.jingleEventHandler(evt); });

        if (this.conferenceEventListener) { this.xmppService.deleteHandler(this.conferenceEventListener); this.conferenceEventListener = undefined; }
        this.conferenceEventListener = this.xmppService.addHandler((stanza: any) => { this.promiseQueue.add(() => this.onConferenceInfo(stanza)); return true; }, 'jabber:iq:conference:2', 'message');

        if (this.dataChannelEventListener) { this.xmppService.deleteHandler(this.dataChannelEventListener); this.dataChannelEventListener = undefined; }
        this.dataChannelEventListener = this.xmppService.addHandler((stanza: any) => { this.onP2PDataChannelMessageReceived(stanza); return true; }, 'jabber:iq:datachannel');
    }

    private async onP2PDataChannelMessageReceived(stanza: any): Promise<void> {
        try {
            const stanzaElem = xmlParser(stanza);
            const commandElem = stanzaElem.find('command');

            // Handle "data-channel-propose" message reception (Receiver side)
            // This message is received by all receiver ressources, only on ressource will responds
            // Allow us to determine the fullJid to establised the dataChannel
            if (commandElem.length && commandElem.attr('node') === 'data-channel-propose') {
                const dataChannel = DataChannel_RB.create();
                dataChannel.id = commandElem.attr('id') as DataChannelID;
                dataChannel.status = DataChannelStatus.INVITED;
                dataChannel.description = commandElem.attr('description');
                dataChannel.publisher = await this.contactService.getContact(commandElem.attr('publisherId'));
                dataChannel.publisherFullJid = stanzaElem.attr('from');
                dataChannel.type = 'p2p';
                this.dataChannels.set(dataChannel.id, dataChannel);
                this.loggerService.infoEvent(`[DataChannelService] onP2PDataChannelMessageReceived (dataChannelId: ${dataChannel.id}, action: data-channel-propose, publisherId: ${dataChannel.publisher.id})`);
                this.sendEvent(DataChannelServiceEvents.ON_DATACHANNEL_AVAILABLE, dataChannel);
            }

            // Handle "data-channel-accept" (Sender side)
            // This IQ is received by the datachannel creator in response of message "data-channel-propose"
            if (commandElem.length && commandElem.attr('node') === 'data-channel-accept') {

                const ack = $iq({ type: 'result', to: stanza.getAttribute('from'), id: stanza.getAttribute('id') });
                this.xmppService.send(ack);

                const dataChannel = this.dataChannels.get(commandElem.attr('id'));
                this.loggerService.infoEvent(`[DataChannelService] onP2PDataChannelMessageReceived (dataChannelId: ${dataChannel.id},action: data-channel-accept)`);

                // Initiate the session
                const jingleSession = await this.xmppService.connection.jingle.initiateDataChannelSession(stanzaElem.attr('from'), this.xmppService.connection.jid);

                // Create RTCDataChannel
                const rtcDataChannel = jingleSession.peerconnection.createDataChannel(dataChannel.description);

                // Update Rainbow DataChannel
                dataChannel.sessionId = jingleSession.sid;
                dataChannel.rtcDataChannel = rtcDataChannel;
                this.attachStatusListeners(dataChannel);

                // Send offer
                jingleSession.sendOffer(true, { id: dataChannel.id, name: 'datachannel', description: dataChannel.description, affiliation: dataChannel.affiliation });
            }

            if (commandElem.length && commandElem.attr('node') === 'data-channel-refuse') {

                const ack = $iq({ type: 'result', to: stanza.getAttribute('from'), id: stanza.getAttribute('id') });
                this.xmppService.send(ack);

                const dataChannel = this.dataChannels.get(commandElem.attr('id'));
                this.loggerService.infoEvent(`[DataChannelService] onP2PDataChannelMessageReceived (dataChannelId: ${dataChannel.id}, action: data-channel-refuse)`);
                dataChannel.status = DataChannelStatus.REFUSED;
            }
        }
        catch (error) {
            this.loggerService.error(`[DataChannelService] onP2PDataChannelMessageReceived -- failure -- ${error.message}`);
        }
    }

    private async onConferenceInfo(stanza: any): Promise<any> {
        try {
            const stanzaElem = xmlParser(stanza);

            const conferenceStateElems = stanzaElem.find('conference-state');
            if (conferenceStateElems.length) {
                const roomId = stanzaElem.find('room-id').text();
                const active = stanzaElem.find('active').text() === 'true';
                if (!active) {

                    //find all data channels related to this conference and remove them
                    this.dataChannels.forEach((dc, key) => {
                        if (dc.bubble?.dbId === roomId) {
                            dc.delete();
                            this.dataChannels.delete(key);
                        }
                    });
                    return true;
                }
            }

            const conferenceInfoElems = stanzaElem.find('conference-info');
            if (conferenceInfoElems.length) {

                //check if we've an active bubble conference session
                const roomId = stanzaElem.find('room-id').text();
                const bubbleConference = this.webConferenceService?.getActiveWebConferenceSession();
                if (!bubbleConference || bubbleConference.id !== roomId) {
                    //ignore this
                    return;
                }

                //check if the conference has ended, in that case delete

                let publisherElems = stanzaElem.find('publishers');
                if (publisherElems.length === 0) publisherElems = stanzaElem.find('added-publishers');
                if (publisherElems.length) {
                    publisherElems.find('publisher').forEach(async (publisherElem: XMLElement) => {
                        // Only take in account application mediaType
                        if (publisherElem.find('media-type').text() === 'application') {
                            const publisherId = publisherElem.find('user-id').text();
                            const id = publisherElem.find('id').text();
                            if (publisherId === this.contactService.userContact.dbId) return;

                            //check if data channel already exists
                            const publisherDc = this.dataChannels.get(publisherElem.find('id').text());
                            if (publisherDc) {
                                //if the data channel is not closed, then ignore this event
                                if (publisherDc.status !== DataChannelStatus.CLOSED) {
                                    //if the bubble conference live transcription is missing, reassign it again
                                    if (id === 'liveTranscription' && !bubbleConference.liveTranscription) {
                                        bubbleConference.liveTranscription = publisherDc;
                                    }
                                    return;
                                }
                            }
                            const dataChannel = DataChannel_RB.create();
                            dataChannel.status = DataChannelStatus.INVITED;
                            dataChannel.id = publisherElem.find('id').text() as DataChannelID;
                            dataChannel.name = publisherElem.find('name').text();
                            dataChannel.description = publisherElem.find('description').text();
                            dataChannel.ressourceId = publisherElem.find('resourceId').text();
                            dataChannel.type = 'janus';
                            dataChannel.bubble = this.bubbleService.getBubbleFromCache(stanzaElem.find('room-id').text());
                            this.dataChannels.set(dataChannel.id, dataChannel);
                            if (id === 'liveTranscription') {
                                bubbleConference.liveTranscription = dataChannel;
                            }
                            else {
                                dataChannel.publisher = await this.contactService.getContact(publisherId);
                                this.loggerService.infoEvent(`[DataChannelService] onConferenceInfo : onDataChannelProposed (id: ${dataChannel.id}, bubbleId : ${dataChannel.bubble.dbId}, publisherId: ${publisherId})`);
                                this.sendEvent(DataChannelServiceEvents.ON_DATACHANNEL_AVAILABLE, dataChannel);
                            }
                        }
                    });
                }

                const removedPublishersElem = stanzaElem.find('removed-publishers');
                if (removedPublishersElem.length) {
                    removedPublishersElem.find('publisher').forEach(async (publisherElem: XMLElement) => {
                        if (publisherElem.find('media-type').text() === 'application') {
                            const dataChannelId = publisherElem.find('id').text();
                            const dataChannel = this.dataChannels.get(dataChannelId);
                            if (dataChannel) {
                                dataChannel.status = DataChannelStatus.CLOSED;
                                this.sendEvent(DataChannelServiceEvents.ON_DATACHANNEL_CLOSED, dataChannel);
                                this.dataChannels.delete(dataChannelId);
                            }
                        }
                    });
                }
                const removedParticipantElems = stanzaElem.find('removed-participants');
                if (removedParticipantElems.length) {
                    if (removedParticipantElems.find('media-type').text() === 'application') { // MCO SOMETHING REALLY WRONG HERE
                        removedParticipantElems.find('user-id').forEach((elem) => {
                            const participantId = elem.text();
                            if (participantId === this.contactService?.userContact?.dbId) {
                                //remove all data channels
                                this.dataChannels.forEach((dataChannel) => {
                                    this.leaveDataChannel(dataChannel);
                                    dataChannel.status = DataChannelStatus.CLOSED;
                                    this.sendEvent(DataChannelServiceEvents.ON_DATACHANNEL_CLOSED, dataChannel);
                                });

                                this.dataChannels.clear();
                            }
                        });
                    }
                }

                const addedSubscriptionElem = stanzaElem.find('added-subscriptions');
                if (addedSubscriptionElem.length) {
                    addedSubscriptionElem.find('subscription').forEach(async (subscriptionElem: XMLElement) => {
                        const subscriberElem = subscriptionElem.find('subscriber').first();
                        const streamElem = subscriptionElem.find('stream').first();
                        if (streamElem && subscriberElem && streamElem.find('media-type').text() === 'application') {
                            const dataChannelId = streamElem.find('id').text();
                            const dataChannel = this.dataChannels.get(dataChannelId);
                            const userId = subscriberElem.find('user-id').text();
                            const user = await this.contactService.getContact(userId);
                            dataChannel.sendEvent(DataChannelEvents.ON_SUBSCRIPTION_ADDED, user);
                        }
                    });
                }

                const removedSubscriptionElem = stanzaElem.find('removed-subscriptions');
                if (removedSubscriptionElem.length) {
                    removedSubscriptionElem.find('subscription').forEach(async (subscriptionElem: XMLElement) => {
                        const subscriberElem = subscriptionElem.find('subscriber').first();
                        const streamElem = subscriptionElem.find('stream').first();
                        if (streamElem && subscriberElem && streamElem.find('media-type').text() === 'application') {
                            const dataChannelId = streamElem.find('id').text();
                            const dataChannel = this.dataChannels.get(dataChannelId);
                            const userId = subscriberElem.find('user-id').text();
                            const user = await this.contactService.getContact(userId);
                            dataChannel.sendEvent(DataChannelEvents.ON_SUBSCRIPTION_REMOVED, user);
                        }
                    });
                }
            }
        }
        catch (error) {
            this.loggerService.error(`[DataChannelService] onConferenceInfo -- failure -- ${error.message}`);
        }
    }

    private async jingleEventHandler(event: RBEvent): Promise<void> {
        try {
            const jingleSession = this.getApplicationSession(event.data.sid);
            if (jingleSession) {
                switch (event.name) {
                    case 'jingleSessionInitiate': this.onJingleSessionInitiate(jingleSession); break;
                    case 'jingleConnectionStateChange': this.onJingleConnectionStateChange(jingleSession); break;
                    case 'jingleError': this.onJingleError(jingleSession, event.data.error); break;
                    default: break;
                }
            }
        }
        catch (error) {
            this.loggerService.info(`[DataChannelService] jingleEventHandler -- failure -- ${event.name} -- ${error.message}`);
        }
    }

    private onJingleSessionInitiate(session: JingleSession): void {
        if (session) {
            // Clean already existing connection
            if (session.application?.id) {
                const dataChannel = this.dataChannels.get(session.application.id);
                if (dataChannel.sessionId && dataChannel.sessionId !== session.sid) {
                    this.loggerService.infoEvent(`[DataChannelService] onJingleSessionInitiate (dataChannelId: ${session.application.id}, sessionId: ${session.sid}) -- clean old session (${dataChannel.sessionId})`);
                    this.xmppService.connection.jingle.terminate(dataChannel.sessionId);
                }
            }

            let dataChannel: DataChannel_RB;
            if (session.application?.id) dataChannel = this.dataChannels.get(session.application.id);
            else dataChannel = Array.from(this.dataChannels.values()).find(dataChannel => (dataChannel.sessionId === session.sid || (session.conference_id && session.conference_id === dataChannel.bubble?.dbId)));
            dataChannel.session = session;

            if (dataChannel?.type === 'p2p') {
                this.loggerService.infoEvent(`[DataChannelService] onJingleSessionInitiate (dataChannelId: ${session.application.id}, sessionId: ${session.sid})`);
                session.peerconnection.ondatachannel = (event: any) => {
                    dataChannel.rtcDataChannel = event.channel;
                    this.attachStatusListeners(dataChannel);
                };
            }

            // Accept session
            session.localType = 'application';
            session.sendAnswer();
        }
    }

    private onJingleConnectionStateChange(session: JingleSession): void {
        if (session?.peerconnection && session.peerconnection.signalingState === 'stable') {
            const connectionState = session.peerconnection.connectionState ? session.peerconnection.connectionState : session.peerconnection.iceConnectionState;

            let dataChannel: DataChannel_RB;
            if (session.application?.id) dataChannel = this.dataChannels.get(session.application.id);
            else dataChannel = Array.from(this.dataChannels.values()).find(dataChannel => (dataChannel.sessionId === session.sid || (session.conference_id && session.conference_id === dataChannel.bubble?.dbId)));

            switch (connectionState) {
                case 'connected':
                case 'completed':
                    if (dataChannel?.type === 'janus') {
                        dataChannel.rtcDataChannel = session.peerconnection.createDataChannel('JanusDataChannel');
                        this.attachStatusListeners(dataChannel);
                    }
                    this.loggerService.infoEvent(`[DataChannelService] onJingleConnectionStateChange (dataChannelId: ${dataChannel?.id}, sessionId: ${session.sid}, connectionState: ${connectionState})`);
                    break;
                case 'disconnected':
                    this.loggerService.infoEvent(`[DataChannelService] onJingleConnectionStateChange (dataChannelId: ${dataChannel?.id}, sessionId: ${session.sid}, connectionState: ${connectionState})`);
                    dataChannel.status = DataChannelStatus.DISCONNECTED;
                    break;
                case 'failed':
                    this.loggerService.infoEvent(`[DataChannelService] onJingleConnectionStateChange (dataChannelId: ${dataChannel?.id}, sessionId: ${session.sid}, connectionState: ${connectionState})`);
                    this.onDataChannelFailed(dataChannel);
                    break;
                default: break;
            }
        }
    }

    private onJingleError(session: JingleSession, error: any) {
        this.loggerService.error(`[DataChannelService] onJingleError (sid: ${session.sid}) -- ${error ? JSON.stringify(error) : ''}`);
    }

    private onDataChannelFailed(dataChannel: DataChannel_RB) {
        this.cleanDataChannel(dataChannel);
        //try to accept again the data channel
        setTimeout (() => {
            //if data channel still exists, try to resubscribe
            if (dataChannel?.id && this.dataChannels.get(dataChannel.id)) {
                this.acceptDataChannelInternal(dataChannel, true);
            }
        }, 200);
    }

    private cleanDataChannel(dataChannel: DataChannel_RB): void {
        try {
            this.loggerService.info(`[DataChannelService] cleanDataChannel (dataChannelId: ${dataChannel?.id})`);
            dataChannel.status = DataChannelStatus.CLOSED;
            this.sendEvent(DataChannelServiceEvents.ON_DATACHANNEL_CLOSED, dataChannel);

            dataChannel.rtcDataChannel?.close();
            dataChannel.session?.peerconnection?.close();
            this.xmppService.connection.jingle.terminate(dataChannel.sessionId);
            dataChannel.session = undefined;
        }
        catch (error) {
            this.loggerService.error(`[DataChannelService] cleanDataChannel (dataChannelId: ${dataChannel?.id}, sessionId: ${dataChannel.sessionId}) -- ${stringifyError(error)}`);
        }
    }

    // **************************************************
    // ** UTILS
    // **************************************************

    private attachStatusListeners(dataChannel: DataChannel_RB): void {
        dataChannel.rtcDataChannel.onopen = () => { this.onDataChannelStatusChange(dataChannel); };
        dataChannel.rtcDataChannel.onclose = () => { this.onDataChannelStatusChange(dataChannel); };
        dataChannel.rtcDataChannel.onerror = () => { this.onDataChannelStatusChange(dataChannel); };
        dataChannel.rtcDataChannel.onmessage = (message) => { if (!message.data) return; dataChannel.sendEvent(DataChannelEvents.ON_MESSAGE_RECEIVED, message.data); };
    }

    private onDataChannelStatusChange(dataChannel: DataChannel_RB): any {
        if (dataChannel.rtcDataChannel?.readyState === 'connecting') dataChannel.status = DataChannelStatus.CONNECTING;
        else if (dataChannel.rtcDataChannel?.readyState === 'open') dataChannel.status = DataChannelStatus.CONNECTED;
        else this.cleanDataChannel(dataChannel);
        this.loggerService.infoEvent(`[DataChannelService] onDataChannelStatusChange (dataChannelId: ${dataChannel.id}, status: ${dataChannel.status})`);
    }

    private sendEvent<const EventName extends keyof IDataChannelServiceEvents>(eventName: EventName, data: IDataChannelServiceEvents[EventName]): void {
        this.eventSubject.next(RBEvent.create(eventName, data));
    }

    private getApplicationSession(sessionId: string): JingleSession {
        const session = this.xmppService.connection.jingle.sessions[sessionId];
        return (session && (session.localType === 'application' || session.remoteType === 'application')) ? session : undefined;
    }
}
